// src/components/Sidebar.js
import React from "react";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  useMediaQuery,
  Switch,
  Stack,
} from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import DashboardIcon from "@mui/icons-material/SpaceDashboardRounded";
import NotificationsIcon from "@mui/icons-material/NotificationsActiveRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ChatIcon from "@mui/icons-material/ChatRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import BoltIcon from "@mui/icons-material/BoltRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";

import { clearAuth } from "../utils/auth";
import SeatsBadge from "./SeatsBadge";
import { useThemeMode } from "../context/ThemeContext";
import { FEATURES } from "../config";

const NAV_WIDTH = 260;

const NavRoot = styled(Box)(({ theme }) => ({
  width: NAV_WIDTH,
  flexShrink: 0,
  height: "100vh",
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 0 8px rgba(0,0,0,0.04)",
  display: "flex",
  flexDirection: "column",
}));

const Brand = styled(Box)(({ theme }) => ({
  padding: "18px 16px 12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const NavHeader = styled(Typography)(({ theme }) => ({
  textTransform: "uppercase",
  letterSpacing: ".08em",
  fontSize: 12,
  color: theme.palette.text.secondary,
  padding: "6px 16px",
}));

const LinkItem = ({ to, icon, label }) => {
  const location = useLocation();

  const isActive =
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      sx={(theme) => ({
        mx: 1,
        borderRadius: 1.5,
        mb: 0.25,
        "& .MuiListItemIcon-root": {
          minWidth: 36,
          color: isActive
            ? theme.palette.primary.main
            : theme.palette.text.secondary,
        },
        ...(isActive
          ? {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              pl: "12px",
            }
          : {
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: "#F2F5F9",
              },
            }),
      })}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontWeight: isActive ? 600 : 500,
          fontSize: 14,
        }}
      />
    </ListItemButton>
  );
};

function SidebarContent({ onLogout }) {
  const { mode, toggleTheme, isDark } = useThemeMode();
  const currentUser = JSON.parse(localStorage.getItem("agentBlayna_user") || "{}");
  const isSuperadmin = currentUser?.role === "superadmin";

  return (
    <>
      {/* Brand */}
      <Brand>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BoltIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={800}>
              AgentBlayna
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary" }}
            >
              Realtor Assistant
            </Typography>
          </Box>
        </Box>

        <Chip
          size="small"
          label="AI Active"
          sx={(t) => ({
            bgcolor: alpha(t.palette.primary.main, 0.12),
            color: t.palette.primary.main,
            fontWeight: 700,
            height: 22,
          })}
        />
      </Brand>

      <Divider />

      <NavHeader>NAVIGATION</NavHeader>

      <List sx={{ px: 0.5 }}>
        <LinkItem
          to="/app/dashboard"
          icon={<DashboardIcon />}
          label="Dashboard"
        />

        <LinkItem
          to="/app/vendors"
          icon={<PeopleAltRoundedIcon />}
          label="Clients"
        />

        {/* Calendar → PredictDisruption page */}
        <LinkItem
          to="/app/predict-disruption"
          icon={<EventRoundedIcon />}
          label="Calendar"
        />

        <LinkItem
          to="/app/listings"
          icon={<HomeWorkRoundedIcon />}
          label="Listings"
        />

        <LinkItem
          to="/app/alerts"
          icon={<NotificationsIcon />}
          label="Tasks & Alerts"
        />

        {/* COMMISSIONS → ForecastTabs page */}
        <LinkItem
          to="/app/commission-forecast"
          icon={<AttachMoneyRoundedIcon />}
          label="Commissions & Revenue"
        />

        <LinkItem to="/app/chat" icon={<ChatIcon />} label="AgentBlayna" />
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      <NavHeader>ACCOUNT</NavHeader>

      <List sx={{ px: 0.5 }}>
        <LinkItem
          to="/app/security"
          icon={<LockResetRoundedIcon />}
          label="Security"
        />

        {FEATURES.ENABLE_BILLING && (
          <>
            <LinkItem
              to="/app/team"
              icon={<GroupsRoundedIcon />}
              label="Team"
            />
            <LinkItem
              to="/app/billing"
              icon={<CreditCardRoundedIcon />}
              label="Billing"
            />
          </>
        )}
        {isSuperadmin && (
          <LinkItem
            to="/admin"
            icon={<AdminPanelSettingsRoundedIcon />}
            label="Admin Panel"
          />
        )}
      </List>

      {/* Seats Badge */}
      {FEATURES.ENABLE_BILLING && (
        <Box sx={{ px: 2, py: 1 }}>
          <SeatsBadge size="medium" />
        </Box>
      )}

      {/* Theme Toggle */}
      <Box
        sx={(theme) => ({
          mx: 2,
          my: 1,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {isDark ? (
              <DarkModeRoundedIcon
                sx={{ fontSize: 18, color: "primary.main" }}
              />
            ) : (
              <LightModeRoundedIcon
                sx={{ fontSize: 18, color: "warning.main" }}
              />
            )}
            <Typography variant="body2" fontWeight={500}>
              {isDark ? "Dark Mode" : "Light Mode"}
            </Typography>
          </Stack>
          <Switch
            size="small"
            checked={isDark}
            onChange={toggleTheme}
            sx={(theme) => ({
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: theme.palette.primary.main,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: theme.palette.primary.main,
              },
            })}
          />
        </Stack>
      </Box>

      <Divider />

      {/* Logout button */}
      <List sx={{ px: 0.5 }}>
        <ListItemButton
          onClick={onLogout}
          sx={(theme) => ({
            mx: 1,
            borderRadius: 1.5,
            mb: 0.5,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main,
              "& .MuiListItemIcon-root": {
                color: theme.palette.error.main,
              },
            },
          })}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontWeight: 500,
              fontSize: 14,
            }}
          />
        </ListItemButton>
      </List>

      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} AgentBlayna
        </Typography>
      </Box>
    </>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  const sidebarContent = (
    <Box
      sx={{
        width: NAV_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <SidebarContent onLogout={handleLogout} />
    </Box>
  );

  // Mobile: Temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: NAV_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: Permanent drawer
  return (
    <NavRoot>
      <SidebarContent onLogout={handleLogout} />
    </NavRoot>
  );
}

